import { useCallback, useEffect, useState } from 'react'

import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { Divider, Tag } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/pro-duotone-svg-icons'
import {
  faBan, faCalendar, faClipboardList, faEnvelope, faGlobe, faGraduationCap, faHospital, faSchool, faStethoscope, faUsers
} from '@fortawesome/pro-solid-svg-icons'

import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import Loading from '../../Components/shared/Loading'
import Mailing from '../../Components/shared/Mailing'
import AnonymousLanguagesMenu from '../InternshipRequestPortal/AnonymousLanguagesMenu'
import { isNonEmptyArray, isObject } from '../../utils'
import { EUROPEAN_DATE } from '../../utils/constants'
import { getTimezoneLessMoment } from '../../utils/momentjs'
import { getSchoolPortalRequestByToken } from '../../utils/api/schoolPortalRequest'
import { getErrorBody } from '../../utils/apiHelper'
import { HttpStatuses } from '../../utils/http'
import {
  countGrantedPlaces, getCoordinatorFullname, getCreatedAt, getStateTag, groupRequests, isClosed,
  summarizeGrantedPeriods
} from '../../utils/entities/schoolPortalRequest'

import '../../assets/index.scss'

const ERROR_PREFIX = 'get_school_portal_request_by_token.error'

const SUPPORTED_ERRORS = [
  'INVALID_TOKEN', 'EXPIRED_TOKEN', 'TOKEN_ALREADY_CONSUMED', 'INVALID_TOKEN_PAYLOAD'
]

const LABEL_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'rgba(0, 0, 0, 0.45)'
}

const VALUE_BOX_STYLE = {
  border: '1px solid #f0f0f0',
  borderRadius: '0.4rem',
  padding: '0.6rem 0.75rem',
  color: 'rgba(0, 0, 0, 0.75)',
  fontWeight: 500
}

const COMMENT_BOX_STYLE = {
  backgroundColor: '#eaf4fb',
  borderLeft: '3px solid #3C8DBC',
  borderRadius: '0.4rem',
  padding: '0.75rem 1rem',
  color: 'rgba(0, 0, 0, 0.65)',
  lineHeight: 1.5
}

const REFUSAL_BOX_STYLE = {
  backgroundColor: '#fff1f0',
  borderLeft: '3px solid #cf1322',
  borderRadius: '0.4rem',
  padding: '0.75rem 1rem',
  color: 'rgba(0, 0, 0, 0.65)',
  lineHeight: 1.5
}

const TOTAL_STYLE = {
  borderTop: '1px solid #f0f0f0',
  paddingTop: '0.6rem',
  color: 'rgba(0, 0, 0, 0.75)',
  fontWeight: 600
}

const HINT_STYLE = {
  fontSize: '0.75em',
  fontWeight: 500,
  color: 'rgba(0, 0, 0, 0.45)'
}

const PERIOD_BADGE_STYLE = {
  backgroundColor: '#eef1f5',
  borderRadius: '0.25rem',
  padding: '0.05rem 0.4rem',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.55)'
}

const SectionLabel = ({ icon, text }) => (
  <span style={LABEL_STYLE}>
    <FontAwesomeIcon icon={icon} style={{ color: '#3C8DBC' }} />
    {text}
  </span>
)

const formatPeriod = period => {
  const startDate = getTimezoneLessMoment(period?.startDate)
  const endDate = getTimezoneLessMoment(period?.endDate)

  return (startDate === null || endDate === null)
    ? '—'
    : `${startDate.format(EUROPEAN_DATE)} → ${endDate.format(EUROPEAN_DATE)}`
}

const formatPeriods = periods => isNonEmptyArray(periods) ? periods.map(formatPeriod).join(' · ') : '—'

const getSectionLabel = line => [line?.sectionToYear?.sectionAbbreviation, line?.sectionToYear?.schoolYearName]
  .filter(part => (part ?? '') !== '')
  .join(' — ')

const DisplaySchoolPortalRequestByToken = () => {
  const t = useSelector(selectTranslate)
  const location = useLocation()

  const [portalRequest, setPortalRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState(null)

  const token = new URLSearchParams(location.search).get('token')

  const handleError = useCallback(error => {
    const errorStatus = {
      icon: faTriangleExclamation,
      color: '#e0a800',
      title: `${ERROR_PREFIX}.generic.title`,
      body: `${ERROR_PREFIX}.generic.body`
    }

    if (![HttpStatuses.NOT_FOUND, HttpStatuses.BAD_REQUEST].includes(error?.status)) {
      setStatus(errorStatus)

      return
    }

    getErrorBody(error).then(({ message }) => {
      if (SUPPORTED_ERRORS.includes(message)) {
        errorStatus.title = `${ERROR_PREFIX}.${message.toLowerCase()}.title`
        errorStatus.body = `${ERROR_PREFIX}.${message.toLowerCase()}.body`
      } else if (message === 'This InternalToken does not exist.') {
        errorStatus.title = `${ERROR_PREFIX}.invalid_token.title`
        errorStatus.body = `${ERROR_PREFIX}.invalid_token.body`
      }

      setStatus(errorStatus)
    }).catch(() => setStatus(errorStatus))
  }, [])

  useEffect(() => {
    if (!token) {
      setLoading(false)
      setPortalRequest(null)
      setStatus({
        icon: faTriangleExclamation,
        title: `${ERROR_PREFIX}.no_token_provided.title`,
        body: `${ERROR_PREFIX}.no_token_provided.body`
      })

      return
    }

    setLoading(true)
    setStatus(null)

    getSchoolPortalRequestByToken(token).then(({ data }) => {
      setPortalRequest(data)
    }).catch(error => {
      handleError(error)
      setPortalRequest(null)
    }).finally(() => setLoading(false))
  }, [token, handleError])

  const stateTag = getStateTag(portalRequest?.state)
  const isAnswered = isClosed(portalRequest)
  const groups = groupRequests(portalRequest?.requests)

  const grantedGroups = groups
    .map(group => ({
      ...group,
      askedPlaces: group.requests.length,
      requests: group.requests.filter(line => isNonEmptyArray(line.grantedPeriods))
    }))
    .filter(group => isNonEmptyArray(group.requests))

  const displayedGroups = isAnswered
    ? grantedGroups
    : groups.map(group => ({ ...group, askedPlaces: group.requests.length }))

  const requestedPlaces = (portalRequest?.requests ?? []).length
  const grantedPlaces = countGrantedPlaces(portalRequest?.requests)

  return (
    <div className='flex-col jc-ctr ai-ctr' style={{ minHeight: '100vh', paddingBlock: '4rem' }}>
      <div className='form-bg' />

      <div className='flex-row w-100 jc-between' style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
        <div style={{ marginLeft: '2rem', width: '100px' }}>
          <img src='/assets/interneo_logo.svg' alt='interneo-logo' />
        </div>
        <AnonymousLanguagesMenu icon={faGlobe} />
      </div>

      <div className='flex-col' style={{ backgroundColor: 'white', borderRadius: '0.5rem', width: '90%', maxWidth: '50rem' }}>
        {isObject(status) && (
          <div className='flex-col ai-ctr pd-16' style={{ textAlign: 'center', gap: '1rem', width: '100%' }}>
            <FontAwesomeIcon icon={status.icon} style={{ fontSize: '3rem', color: status.color ?? '#e0a800' }} />
            <h2 style={{ margin: 0, width: '100%' }}>{t(status.title)}</h2>
            <p style={{ margin: 0, width: '100%', lineHeight: 1.5, color: 'rgba(0, 0, 0, 0.55)' }}>
              {status.body && t(status.body)}
            </p>
          </div>
        )}

        {token && loading && <div className='pd-16'><Loading size='3x' /></div>}

        {isObject(portalRequest) && !loading && (
          <>
            <div className='pd-16 flex-row ai-ctr jc-between' style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
              <h3 className='flex-row ai-ctr m-0' style={{ color: 'rgba(0, 0, 0, 0.65)', gap: '0.5rem' }}>
                <FontAwesomeIcon icon={faClipboardList} style={{ fontSize: '1.4rem', color: '#3C8DBC' }} />
                <b>{t('school_portal.tracking.title')}</b>
              </h3>
              <Tag color={stateTag.color}>{t(stateTag.label)}</Tag>
            </div>

            <Divider style={{ width: 'auto', margin: '0px' }} type='horizontal' />

            <div className='flex-col pd-16' style={{ gap: '1rem' }}>
              <div className='flex-row' style={{ gap: '1rem', flexWrap: 'wrap' }}>
                <div className='flex-col' style={{ ...VALUE_BOX_STYLE, gap: '0.4rem', flex: '1 1 14rem', minWidth: 0 }}>
                  <SectionLabel icon={faSchool} text={t('common.school')} />
                  {portalRequest.school?.name ?? '—'}
                </div>
                <div className='flex-col' style={{ ...VALUE_BOX_STYLE, gap: '0.4rem', flex: '1 1 14rem', minWidth: 0 }}>
                  <SectionLabel icon={faHospital} text={t('common.institution')} />
                  {portalRequest.institution?.name ?? '—'}
                </div>
              </div>

              <div className='flex-row' style={{ gap: '1rem', flexWrap: 'wrap' }}>
                <div className='flex-col' style={{ ...VALUE_BOX_STYLE, gap: '0.4rem', flex: '1 1 14rem', minWidth: 0 }}>
                  <SectionLabel icon={faEnvelope} text={t('school_portal.tracking.requester')} />
                  <span>{getCoordinatorFullname(portalRequest.coordinator)}</span>
                  {portalRequest.coordinator?.email && <Mailing email={portalRequest.coordinator.email} />}
                </div>
                <div className='flex-col' style={{ ...VALUE_BOX_STYLE, gap: '0.4rem', flex: '1 1 14rem', minWidth: 0 }}>
                  <SectionLabel icon={faCalendar} text={t('data_table.column_title.creation_date')} />
                  {getCreatedAt(portalRequest) ?? '—'}
                </div>
              </div>

              {(portalRequest.refusalReason ?? '') !== '' && (
                <div style={REFUSAL_BOX_STYLE}>
                  <div className='flex-row ai-ctr' style={{ gap: '0.4rem', fontWeight: 600 }}>
                    <FontAwesomeIcon icon={faBan} />
                    {t('school_portal.tracking.refusal_reason')}
                  </div>
                  <div className='mt-4'>{portalRequest.refusalReason}</div>
                </div>
              )}
            </div>

            <Divider style={{ width: 'auto', margin: '0px' }} type='horizontal' />

            <div className='flex-col pd-16' style={{ gap: '0.75rem' }}>
              <SectionLabel icon={faUsers} text={t('school_portal.tracking.lines')} />

              {displayedGroups.map(group => {
                const sample = group.requests[0] ?? group.sample
                const requestedPeriods = sample.requestPeriods ?? []
                const periods = isAnswered ? summarizeGrantedPeriods(group.requests) : requestedPeriods
                const places = isAnswered ? countGrantedPlaces(group.requests) : group.requests.length
                const changedPeriods = isAnswered && formatPeriods(periods) !== formatPeriods(requestedPeriods)
                const changedPlaces = isAnswered && places !== group.askedPlaces

                return (
                  <div key={group.key} className='flex-col' style={{ ...VALUE_BOX_STYLE, gap: '0.5rem' }}>
                    <div className='flex-row ai-ctr jc-between' style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span className='flex-row ai-ctr' style={{ gap: '0.4rem' }}>
                        <FontAwesomeIcon icon={faGraduationCap} style={{ color: '#3C8DBC' }} />
                        <b>{getSectionLabel(sample) || '—'}</b>
                      </span>
                      {(changedPeriods || changedPlaces) && (
                        <Tag color='orange'>{t('school_portal.tracking.changed')}</Tag>
                      )}
                    </div>

                    <div className='flex-row ai-ctr' style={{ gap: '1rem', flexWrap: 'wrap' }}>
                      <span className='flex-row ai-ctr' style={{ gap: '0.4rem' }}>
                        <FontAwesomeIcon icon={faUsers} />
                        {t('school_portal.tracking.places', { places })}
                        {changedPlaces && (
                          <span style={HINT_STYLE}>
                            {t('school_portal.tracking.requested_value', {
                              value: t('school_portal.tracking.places', { places: group.askedPlaces })
                            })}
                          </span>
                        )}
                      </span>
                      <span className='flex-row ai-ctr' style={{ gap: '0.4rem' }}>
                        <FontAwesomeIcon icon={faStethoscope} />
                        {sample.sector?.name ?? '—'}
                      </span>
                    </div>

                    <div className='flex-col' style={{ gap: '0.25rem' }}>
                      {periods.map((period, index) => (
                        <div key={period.signature ?? period.id ?? index} className='flex-row ai-ctr' style={{ gap: '0.5rem' }}>
                          <span style={PERIOD_BADGE_STYLE}>P{index + 1}</span>
                          <span style={{ color: '#3C8DBC', fontWeight: 600 }}>{formatPeriod(period)}</span>
                          {isAnswered && periods.length > 1 && (
                            <span style={HINT_STYLE}>
                              {t('school_portal.tracking.places', { places: period.places })}
                            </span>
                          )}
                        </div>
                      ))}
                      {changedPeriods && (
                        <span style={HINT_STYLE}>
                          {t('school_portal.tracking.requested_value', { value: formatPeriods(requestedPeriods) })}
                        </span>
                      )}
                    </div>

                    {(sample.comment ?? '') !== '' && (
                      <div style={COMMENT_BOX_STYLE}>{sample.comment}</div>
                    )}
                  </div>
                )
              })}

              {!isNonEmptyArray(displayedGroups) && (
                <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                  {t(isAnswered ? 'school_portal.tracking.nothing_granted' : 'school_portal.tracking.no_line')}
                </span>
              )}

              {isNonEmptyArray(groups) && (
                <div className='flex-row ai-ctr jc-between' style={TOTAL_STYLE}>
                  <span>{t(isAnswered ? 'school_portal.tracking.total_granted' : 'school_portal.tracking.total_asked')}</span>
                  <span>
                    {t('school_portal.tracking.places', { places: isAnswered ? grantedPlaces : requestedPlaces })}
                    {isAnswered && (
                      <span style={HINT_STYLE}>
                        {t('school_portal.tracking.total_requested', { requested: requestedPlaces })}
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default DisplaySchoolPortalRequestByToken
